import { useState } from 'react';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import CardAttribute from '../../../../../components/card-attribute/CardAttribute';
import { buildTitle, isLotItem } from '../../../../../utils/whatnot/whatnotListingText';

import {
    ListingPanelContainer,
    ListingRowContainer,
    ListingNestedRow,
    ListingSwatch,
    ListingLotBadge,
    ListingInfo,
    ListingName,
    LotCardList,
    RowActions,
    ShippingDropdown,
} from './ListingPanel.styles.js';

function buildParallelText(item) {
    const name = item.cardParallelName ?? 'BASE';
    if (item.cardPrintNumber != null && item.cardSupply != null)
        return `${name} #${item.cardPrintNumber}/${item.cardSupply}`;
    if (item.cardSupply != null)
        return `${name} /${item.cardSupply}`;
    return name;
}

function ShippingSelect({ value, onChange }) {
    return (
        <ShippingDropdown
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            title="Shipping profile for this listing"
        >
            {!value && <option value="">— Shipping —</option>}
            <option value="0-1 oz">0-1 oz</option>
            <option value="1-3 oz">1-3 oz</option>
            <option value="4-7 oz">4-7 oz</option>
            <option value="8-12 oz">8-12 oz</option>
            <option value="13-16 oz">13-16 oz</option>
            <option value="1-2 lbs">1-2 lbs</option>
            <option value="2-3 lbs">2-3 lbs</option>
        </ShippingDropdown>
    );
}

function SingleRow({ item, isSelected, onToggleSelect, onRemove, onUpdateShipping }) {
    const playerName   = Array.isArray(item.name) ? item.name.join(' & ') : (item.name ?? '');
    const parallelText = buildParallelText(item);

    return (
        <ListingRowContainer $selected={isSelected} onClick={() => onToggleSelect(item.id)}>
            <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggleSelect(item.id)}
                onClick={(e) => e.stopPropagation()}
                style={{ accentColor: 'rgba(255,255,255,0.85)', cursor: 'pointer', flexShrink: 0, height: 15, width: 15 }}
            />
            <ListingSwatch $color={item.cardColor ?? '#fff'} />
            <ListingInfo>
                <ListingName>{playerName}</ListingName>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {item.series && <CardAttribute text={item.series} size="S" />}
                    {item.cardNumber && <CardAttribute text={`#${item.cardNumber}`} size="S" />}
                    <CardAttribute text={parallelText} color={item.cardColor ?? null} size="S" />
                </div>
            </ListingInfo>
            <RowActions>
                <ShippingSelect value={item.shippingProfile} onChange={(v) => onUpdateShipping(item.id, v)} />
                <CardAttribute
                    text="REMOVE"
                    color="#ef4444"
                    onClick={(e) => { e.stopPropagation(); onRemove(item.id); }}
                />
            </RowActions>
        </ListingRowContainer>
    );
}

function NestedCardRow({ card }) {
    const parallelText = buildParallelText(card);
    return (
        <ListingNestedRow>
            <ListingSwatch $color={card.cardColor ?? '#fff'} />
            <ListingInfo>
                <ListingName>{buildTitle(card)}</ListingName>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {card.series && <CardAttribute text={card.series} size="S" />}
                    {card.cardNumber && <CardAttribute text={`#${card.cardNumber}`} size="S" />}
                    <CardAttribute text={parallelText} color={card.cardColor ?? null} size="S" />
                </div>
            </ListingInfo>
        </ListingNestedRow>
    );
}

function LotRow({ item, isSelected, onToggleSelect, onRemove, onUngroup, onUpdateShipping }) {
    const [expanded, setExpanded] = useState(false);
    const cardCount = item.cards?.length ?? item.cardCount ?? 0;

    const toggleExpand = (e) => {
        e.stopPropagation();
        setExpanded((prev) => !prev);
    };

    return (
        <>
            <ListingRowContainer $selected={isSelected} onClick={() => onToggleSelect(item.id)}>
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect(item.id)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ accentColor: 'rgba(255,255,255,0.85)', cursor: 'pointer', flexShrink: 0, height: 15, width: 15 }}
                />
                <ListingLotBadge onClick={toggleExpand} title={expanded ? 'Collapse' : 'Expand'}>
                    {expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                </ListingLotBadge>
                <ListingInfo>
                    <ListingName>{item.title ?? 'Untitled Lot'}</ListingName>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <CardAttribute text="LOT" size="S" />
                        <CardAttribute text={`${cardCount} ${cardCount === 1 ? 'card' : 'cards'}`} size="S" />
                    </div>
                </ListingInfo>
                <RowActions>
                    <CardAttribute text="UNGROUP" onClick={(e) => { e.stopPropagation(); onUngroup(item); }} />
                    <ShippingSelect value={item.shippingProfile} onChange={(v) => onUpdateShipping(item.id, v)} />
                    <CardAttribute text="REMOVE" color="#ef4444" onClick={(e) => { e.stopPropagation(); onRemove(item.id); }} />
                </RowActions>
            </ListingRowContainer>
            {expanded && (
                <LotCardList>
                    {(item.cards ?? []).map((card, index) => (
                        <NestedCardRow key={`${item.id}-${index}`} card={card} />
                    ))}
                </LotCardList>
            )}
        </>
    );
}

export default function ListingPanel({ items, onRemove, onUngroup, onUpdateShipping, selectedIds, onToggleSelect }) {
    return (
        <ListingPanelContainer>
            {items.map((item) => (
                isLotItem(item) ? (
                    <LotRow
                        key={item.id}
                        item={item}
                        isSelected={selectedIds.has(item.id)}
                        onToggleSelect={onToggleSelect}
                        onRemove={onRemove}
                        onUngroup={onUngroup}
                        onUpdateShipping={onUpdateShipping}
                    />
                ) : (
                    <SingleRow
                        key={item.id}
                        item={item}
                        isSelected={selectedIds.has(item.id)}
                        onToggleSelect={onToggleSelect}
                        onRemove={onRemove}
                        onUpdateShipping={onUpdateShipping}
                    />
                )
            ))}
        </ListingPanelContainer>
    );
}
